import { useContext, useRef } from 'react';
import { TestTagContext } from '../contexts/test-tag.context';

type TagCreatorFunction = (appendText?: string) => (markupRef: HTMLElement | null) => void;

/**
 * A hook that returns a function that is used to create a testing tag on a particular element.
 *
 * Usage:
 *  const tagCreator = useTestTag(label, name);
 *
 *  <div ref={tagCreator('appended-text')} />
 *
 * @param {Array<string>} tagParts - The properties used to create a testing tag for an element.
 * @returns {TagCreatorFunction} A function that accepts an HTMLElement reference.
 */
const useTestTag = (...tagParts: Array<string>): TagCreatorFunction => {
  const baseTag = useRef<string>();
  const tagCreator = useRef<TagCreatorFunction>();
  const testingContext = useContext(TestTagContext);

  if (!baseTag.current) {
    baseTag.current = '';
    for (let i = 0, length = tagParts.length; i < length; i++) {
      baseTag.current += i ? '-' : '' + tagParts[i].replace(/\s+/g, '-').toLowerCase();
    }
  }

  if (!tagCreator.current) {
    tagCreator.current = (appendText = '') => {
      // if the testing context is not setup correctly or isTesting is set to false then return an empty function
      if (!testingContext || !testingContext.isTesting) {
        return () => {};
      } else {
        const finalTag =
          appendText && appendText.length > 0 ? `${baseTag.current || ''}-${appendText}` : baseTag.current || '';
        return (markupRef) => {
          if (markupRef) {
            markupRef.setAttribute('data-testid', finalTag);
          }
        };
      }
    };
  }

  return tagCreator.current;
};

export default useTestTag;
